import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/use-subscription";
import {
  Loader2,
  Users,
  Lock,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Crown,
  Eye,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

type ContextType = { nursingHome: NursingHome };

type LeadSourceType = "waitlist_submission" | "nursing_home_lead";

interface Lead {
  source_type: LeadSourceType;
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  preferred_location: string[];
  specific_nursing_home: string | null;
  lead_status: string;
  created_at: string;
  timeframe: string | null;
  care_needs: string[] | null;
}

type FeedbackMap = Record<
  string,
  { rating: "useful" | "not_useful"; note: string | null }
>;

function leadKey(sourceType: string, id: string) {
  return `${sourceType}:${id}`;
}

export default function LeadsPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const navigate = useNavigate();
  const {
    hasLeadManagement,
    subscriptionActive,
    plan_name,
    plan_tier,
    loading: subscriptionLoading,
  } = useSubscription();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadCount, setLeadCount] = useState(0);
  const [feedbackByLead, setFeedbackByLead] = useState<FeedbackMap>({});
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadFeedback = useCallback(async () => {
    const { data, error } = await supabase
      .from("owner_lead_feedback")
      .select("source_type, source_id, rating, note")
      .eq("nursing_home_id", nursingHome.nh_slug);
    if (error) {
      console.warn("owner_lead_feedback:", error);
      return;
    }
    const next: FeedbackMap = {};
    const draftPatch: Record<string, string> = {};
    for (const row of data ?? []) {
      if (row.rating !== "useful" && row.rating !== "not_useful") continue;
      const k = leadKey(row.source_type, row.source_id);
      next[k] = {
        rating: row.rating,
        note: row.note,
      };
      if (row.note) draftPatch[k] = row.note;
    }
    setFeedbackByLead(next);
    if (Object.keys(draftPatch).length > 0) {
      setNoteDrafts((d) => ({ ...d, ...draftPatch }));
    }
  }, [nursingHome.nh_slug]);

  const fetchLeads = useCallback(async () => {
    try {
      const { data: countRows, error: countError } = await supabase.rpc(
        "get_owner_lead_signal_counts",
        { p_nh_slug: nursingHome.nh_slug },
      );
      if (countError) throw countError;
      const row = countRows?.[0];
      const totalCount =
        Number(row?.direct_count ?? 0) + Number(row?.waitlist_count ?? 0);
      setLeadCount(totalCount);

      if (hasLeadManagement) {
        const { data: inbox, error: inboxError } = await supabase.rpc(
          "list_owner_inbox_leads",
          { p_nh_slug: nursingHome.nh_slug },
        );
        if (inboxError) throw inboxError;

        const mapped: Lead[] = (inbox ?? []).map((r) => ({
          source_type: r.source_type as LeadSourceType,
          id: r.source_id,
          first_name: r.first_name ?? "",
          last_name: r.last_name ?? "",
          email: r.email ?? null,
          phone: r.phone ?? null,
          preferred_location: r.preferred_location ?? [],
          specific_nursing_home: r.specific_nursing_home ?? null,
          lead_status: r.lead_status ?? "pending",
          created_at: r.created_at,
          timeframe: r.timeframe ?? null,
          care_needs: r.care_needs ?? null,
        }));
        setLeads(mapped);
        await loadFeedback();
      } else {
        setLeads([]);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [
    nursingHome.nh_slug,
    hasLeadManagement,
    loadFeedback,
  ]);

  useEffect(() => {
    setLoading(true);
    fetchLeads();
  }, [fetchLeads]);

  const submitFeedback = async (lead: Lead, rating: "useful" | "not_useful") => {
    const key = leadKey(lead.source_type, lead.id);
    const note = (noteDrafts[key] ?? "").trim();
    setSavingKey(key);
    try {
      const { error } = await supabase.rpc("upsert_owner_lead_feedback", {
        p_nursing_home_id: nursingHome.nh_slug,
        p_source_type: lead.source_type,
        p_source_id: lead.id,
        p_rating: rating,
        p_note: note,
      });
      if (error) throw error;
      toast.success("Thanks — your feedback was saved.");
      setFeedbackByLead((prev) => ({
        ...prev,
        [key]: { rating, note: note || null },
      }));
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : "Could not save feedback";
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Lead Management</h1>
          <p className="text-muted-foreground">
            View and manage potential residents interested in {nursingHome.center_name}
          </p>
        </div>
      </div>

      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-full p-3 bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Leads Available</p>
                <p className="text-3xl font-bold">{leadCount}</p>
              </div>
            </div>
            {!hasLeadManagement && leadCount > 0 && (
              <Badge variant="secondary" className="text-sm">
                <Lock className="h-3 w-3 mr-1" />
                {subscriptionActive ? "Plan upgrade" : "Subscription"}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {!hasLeadManagement && leadCount > 0 ? (
        <Card className="border-2 border-dashed border-primary/30">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Crown className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl">Unlock {leadCount} Potential Residents</CardTitle>
            <CardDescription className="text-base max-w-md mx-auto">
              {subscriptionActive
                ? `Your ${plan_name ?? "current"} plan does not include full lead access. Upgrade to view contact details and connect with families searching in your area.`
                : "Subscribe to view contact details and connect with families actively searching for care in your area."}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <div className="space-y-3 opacity-60">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg blur-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-muted" />
                    <div>
                      <div className="h-4 w-32 bg-muted rounded" />
                      <div className="h-3 w-24 bg-muted rounded mt-1" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-8 w-20 bg-muted rounded" />
                    <div className="h-8 w-20 bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Button size="lg" onClick={() => navigate("/owner/subscription")}>
                <Crown className="mr-2 h-4 w-4" />
                View Subscription Plans
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/owner/subscription")}>
                Starting at €29/month
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              Each lead represents a family actively searching for nursing home care.
              <br />
              Don&apos;t miss out on connecting with potential residents.
            </p>
          </CardContent>
        </Card>
      ) : hasLeadManagement && leads.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Leads</h2>
            <Badge variant="outline" className="text-primary">
              <Eye className="h-3 w-3 mr-1" />
              {plan_tier?.charAt(0).toUpperCase()}
              {plan_tier?.slice(1)} Access
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground">
            Mark whether each lead was useful for your home. Optional note (max 500 characters) helps
            us improve matching.
          </p>

          <div className="grid gap-4">
            {leads.map((lead) => {
              const key = leadKey(lead.source_type, lead.id);
              const existing = feedbackByLead[key];
              const busy = savingKey === key;
              const fi = lead.first_name?.charAt(0) || "?";
              const li = lead.last_name?.charAt(0) || "?";

              return (
                <Card key={key} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-semibold text-primary">
                            {fi}
                            {li}
                          </span>
                        </div>
                        <div className="space-y-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold">
                              {lead.first_name} {lead.last_name}
                            </p>
                            <Badge variant="outline" className="text-xs font-normal">
                              {lead.source_type === "waitlist_submission"
                                ? "Waitlist"
                                : "Direct enquiry"}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                            {lead.email ? (
                              <span className="flex items-center gap-1 min-w-0">
                                <Mail className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{lead.email}</span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground/70">No email on file</span>
                            )}
                            {lead.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {lead.phone}
                              </span>
                            )}
                          </div>
                          {lead.preferred_location.length > 0 && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              {lead.preferred_location
                                .map((l) => l.charAt(0).toUpperCase() + l.slice(1))
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-stretch md:items-end gap-2 shrink-0">
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge
                            variant={lead.lead_status === "pending" ? "secondary" : "default"}
                          >
                            {lead.lead_status}
                          </Badge>
                          {lead.timeframe && <Badge variant="outline">{lead.timeframe}</Badge>}
                        </div>
                        <span className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(lead.created_at), "MMM d, yyyy")}
                        </span>
                        <div className="flex gap-2 flex-wrap justify-end">
                          {lead.email ? (
                            <Button size="sm" variant="outline" asChild>
                              <a href={`mailto:${lead.email}`}>
                                <Mail className="h-3 w-3 mr-1" />
                                Email
                              </a>
                            </Button>
                          ) : null}
                          {lead.phone && (
                            <Button size="sm" variant="outline" asChild>
                              <a href={`tel:${lead.phone}`}>
                                <Phone className="h-3 w-3 mr-1" />
                                Call
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {lead.care_needs && lead.care_needs.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm text-muted-foreground mb-2">Care Needs:</p>
                        <div className="flex flex-wrap gap-1">
                          {lead.care_needs.map((need, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {need}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t space-y-3">
                      <Label className="text-sm font-medium">Was this lead useful?</Label>
                      {existing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={existing.rating === "useful" ? "default" : "secondary"}
                            className={
                              existing.rating === "useful"
                                ? "bg-green-700 hover:bg-green-700"
                                : ""
                            }
                          >
                            {existing.rating === "useful" ? "Marked useful" : "Marked not useful"}
                          </Badge>
                          {existing.note ? (
                            <span className="text-sm text-muted-foreground italic">
                              “{existing.note}”
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      <Textarea
                        placeholder="Optional note for our team (e.g. wrong county, not ready to move)…"
                        maxLength={500}
                        rows={2}
                        disabled={busy}
                        value={noteDrafts[key] ?? ""}
                        onChange={(e) =>
                          setNoteDrafts((d) => ({ ...d, [key]: e.target.value }))
                        }
                        className="resize-none text-sm"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={existing?.rating === "useful" ? "default" : "outline"}
                          className={
                            existing?.rating === "useful"
                              ? "bg-green-700 hover:bg-green-700"
                              : ""
                          }
                          disabled={busy}
                          onClick={() => submitFeedback(lead, "useful")}
                        >
                          {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ThumbsUp className="h-4 w-4 mr-1" />
                          )}
                          Useful
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={existing?.rating === "not_useful" ? "destructive" : "outline"}
                          disabled={busy}
                          onClick={() => submitFeedback(lead, "not_useful")}
                        >
                          {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ThumbsDown className="h-4 w-4 mr-1" />
                          )}
                          Not useful
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ) : hasLeadManagement && leadCount > 0 && leads.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="h-12 w-12 mx-auto opacity-50 mb-4" />
            <p className="max-w-md mx-auto">
              We&apos;re showing {leadCount} potential interest in your area. Eligible lead records
              will appear here as they match your nursing home and county.
            </p>
          </CardContent>
        </Card>
      ) : leadCount === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Leads Yet</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              When families express interest in care homes in {nursingHome.county}, their inquiries
              will appear here.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
